import _ from 'lodash';
import { AnyAction } from 'redux';
import {
    ChipMonthlyAggregate,
    EmployeeChip,
    EmployeeChipHistory,
    MonthlyOrgAggregate,
    OrgChipOverview,
    VendingMachine,
    VendingOrg,
    VendingOrgMembership,
    VendingPerson,
} from 'src/entities/chip/EmployeeChip';
import {
    VendingOrgSettlementConfig,
    VendingProductBillingConfig,
    VendingSettlementParty,
} from 'src/entities/chip/VendingSettlement';
import { entityCollectionDefaultState, EntityCollectionState } from 'src/store/entities/types/EntityCollectionState';
import { singleEntityDefaultState, SingleEntityState } from 'src/store/entities/types/SingleEntityState';
import {
    FAILURE_CHIP,
    FAILURE_CHIP_HISTORY,
    FAILURE_CHIP_MACHINES,
    FAILURE_CHIP_MUTATION,
    FAILURE_CHIP_TRANSACTIONS,
    FAILURE_CHIP_YEARLY,
    FAILURE_CUSTOMER_MEMBERSHIPS,
    FAILURE_MY_CHIPS,
    FAILURE_MY_MACHINES,
    FAILURE_MY_TRANSACTIONS,
    FAILURE_MY_YEARLY,
    FAILURE_ORG_CHIPS,
    FAILURE_ORG_MACHINES,
    FAILURE_ORG_MEMBERS,
    FAILURE_ORG_PERSONS,
    FAILURE_ORG_OVERVIEW,
    FAILURE_ORG_TRANSACTIONS,
    FAILURE_ORG_YEARLY,
    FAILURE_PRODUCT_BILLING,
    FAILURE_SETTLEMENT_CONFIG,
    FAILURE_SETTLEMENT_PARTIES,
    FAILURE_UNMAPPED_PRODUCTS,
    FAILURE_VENDING_ORGS,
    REQUEST_CHIP,
    REQUEST_CHIP_HISTORY,
    REQUEST_CHIP_MACHINES,
    REQUEST_CHIP_MUTATION,
    REQUEST_CHIP_TRANSACTIONS,
    REQUEST_CHIP_YEARLY,
    REQUEST_CUSTOMER_MEMBERSHIPS,
    REQUEST_MY_CHIPS,
    REQUEST_MY_MACHINES,
    REQUEST_MY_TRANSACTIONS,
    REQUEST_MY_YEARLY,
    REQUEST_ORG_CHIPS,
    REQUEST_ORG_MACHINES,
    REQUEST_ORG_MEMBERS,
    REQUEST_ORG_PERSONS,
    REQUEST_ORG_OVERVIEW,
    REQUEST_ORG_TRANSACTIONS,
    REQUEST_ORG_YEARLY,
    REQUEST_PRODUCT_BILLING,
    REQUEST_SETTLEMENT_CONFIG,
    REQUEST_SETTLEMENT_PARTIES,
    REQUEST_UNMAPPED_PRODUCTS,
    REQUEST_VENDING_ORGS,
    SUCCESS_CHIP,
    SUCCESS_CHIP_HISTORY,
    SUCCESS_CHIP_MACHINES,
    SUCCESS_CHIP_MUTATION,
    SUCCESS_CHIP_TRANSACTIONS,
    SUCCESS_CHIP_YEARLY,
    SUCCESS_CUSTOMER_MEMBERSHIPS,
    SUCCESS_MY_CHIPS,
    SUCCESS_MY_MACHINES,
    SUCCESS_MY_TRANSACTIONS,
    SUCCESS_MY_YEARLY,
    SUCCESS_ORG_CHIPS,
    SUCCESS_ORG_MACHINES,
    SUCCESS_ORG_MEMBERS,
    SUCCESS_ORG_PERSONS,
    SUCCESS_ORG_OVERVIEW,
    SUCCESS_ORG_TRANSACTIONS,
    SUCCESS_ORG_YEARLY,
    SUCCESS_PRODUCT_BILLING,
    SUCCESS_SETTLEMENT_CONFIG,
    SUCCESS_SETTLEMENT_PARTIES,
    SUCCESS_UNMAPPED_PRODUCTS,
    SUCCESS_VENDING_ORGS,
} from './actions';

function collectionReducer<G>(requestType: string, successType: string, failureType: string) {
    return (state: EntityCollectionState<G> = entityCollectionDefaultState<G>(), action: AnyAction) => {
        switch (action.type) {
            case requestType:
                return _.assign({}, state, { isFetching: true, error: undefined });
            case successType:
                return _.assign({}, state, { isFetching: false, items: action.response.data, error: undefined });
            case failureType:
                return _.assign({}, state, { isFetching: false, items: [], error: action.error });
            default:
                return state;
        }
    };
}

function singleReducer<G>(requestType: string, successType: string, failureType: string) {
    return (state: SingleEntityState<G> = singleEntityDefaultState<G>(), action: AnyAction) => {
        switch (action.type) {
            case requestType:
                return _.assign({}, state, { isFetching: true, error: undefined });
            case successType:
                return _.assign({}, state, { isFetching: false, content: action.response.data, error: undefined });
            case failureType:
                return _.assign({}, state, { isFetching: false, content: undefined, error: action.error });
            default:
                return state;
        }
    };
}

/* Vending Orgs */
export const vendingOrgsReducer = collectionReducer<VendingOrg>(
    REQUEST_VENDING_ORGS,
    SUCCESS_VENDING_ORGS,
    FAILURE_VENDING_ORGS
);

/* My Chips */
export const myChipsReducer = collectionReducer<EmployeeChip>(REQUEST_MY_CHIPS, SUCCESS_MY_CHIPS, FAILURE_MY_CHIPS);

/* Customer Vending Memberships (admin: vending role/orgs of viewed customer) */
export const customerMembershipsReducer = collectionReducer<VendingOrgMembership>(
    REQUEST_CUSTOMER_MEMBERSHIPS,
    SUCCESS_CUSTOMER_MEMBERSHIPS,
    FAILURE_CUSTOMER_MEMBERSHIPS
);

/* Org Members (machine-scoped viewer access management) */
export const orgMembersReducer = collectionReducer<VendingOrgMembership>(
    REQUEST_ORG_MEMBERS,
    SUCCESS_ORG_MEMBERS,
    FAILURE_ORG_MEMBERS
);

/* Org Chips (paged response) */
export const orgChipsReducer = singleReducer<any>(REQUEST_ORG_CHIPS, SUCCESS_ORG_CHIPS, FAILURE_ORG_CHIPS);

/* Org Overview */
export const orgOverviewReducer = singleReducer<OrgChipOverview>(
    REQUEST_ORG_OVERVIEW,
    SUCCESS_ORG_OVERVIEW,
    FAILURE_ORG_OVERVIEW
);

/* Org Yearly (one row per month) */
export const orgYearlyReducer = collectionReducer<MonthlyOrgAggregate>(
    REQUEST_ORG_YEARLY,
    SUCCESS_ORG_YEARLY,
    FAILURE_ORG_YEARLY
);

/* Org Transactions (paged response) */
export const orgTransactionsReducer = singleReducer<any>(
    REQUEST_ORG_TRANSACTIONS,
    SUCCESS_ORG_TRANSACTIONS,
    FAILURE_ORG_TRANSACTIONS
);

/* Org Machines (filter options + registry) */
export const orgMachinesReducer = collectionReducer<VendingMachine>(
    REQUEST_ORG_MACHINES,
    SUCCESS_ORG_MACHINES,
    FAILURE_ORG_MACHINES
);

/* Org Persons (chip-reassignment picker) */
export const orgPersonsReducer = collectionReducer<VendingPerson>(
    REQUEST_ORG_PERSONS,
    SUCCESS_ORG_PERSONS,
    FAILURE_ORG_PERSONS
);

/* Chip Detail */
export const chipDetailReducer = singleReducer<EmployeeChip>(REQUEST_CHIP, SUCCESS_CHIP, FAILURE_CHIP);

/* Chip Transactions (paged response) */
export const chipTransactionsReducer = singleReducer<any>(
    REQUEST_CHIP_TRANSACTIONS,
    SUCCESS_CHIP_TRANSACTIONS,
    FAILURE_CHIP_TRANSACTIONS
);

/* Chip Yearly (per-chip monthly overview) */
export const chipYearlyReducer = collectionReducer<ChipMonthlyAggregate>(
    REQUEST_CHIP_YEARLY,
    SUCCESS_CHIP_YEARLY,
    FAILURE_CHIP_YEARLY
);

/* Chip Machines (filter options) */
export const chipMachinesReducer = collectionReducer<VendingMachine>(
    REQUEST_CHIP_MACHINES,
    SUCCESS_CHIP_MACHINES,
    FAILURE_CHIP_MACHINES
);

/* My Transactions (caller-scoped, paged response) */
export const myTransactionsReducer = singleReducer<any>(
    REQUEST_MY_TRANSACTIONS,
    SUCCESS_MY_TRANSACTIONS,
    FAILURE_MY_TRANSACTIONS
);

/* My Machines (filter options across all own chips) */
export const myMachinesReducer = collectionReducer<VendingMachine>(
    REQUEST_MY_MACHINES,
    SUCCESS_MY_MACHINES,
    FAILURE_MY_MACHINES
);

/* My Yearly (caller-scoped monthly overview) */
export const myYearlyReducer = collectionReducer<ChipMonthlyAggregate>(
    REQUEST_MY_YEARLY,
    SUCCESS_MY_YEARLY,
    FAILURE_MY_YEARLY
);

/* Chip History */
export const chipHistoryReducer = collectionReducer<EmployeeChipHistory>(
    REQUEST_CHIP_HISTORY,
    SUCCESS_CHIP_HISTORY,
    FAILURE_CHIP_HISTORY
);

/* Chip Mutation */
export const chipMutationReducer = singleReducer<any>(
    REQUEST_CHIP_MUTATION,
    SUCCESS_CHIP_MUTATION,
    FAILURE_CHIP_MUTATION
);

/* Settlement Config (Abrechnung — org fees) */
export const settlementConfigReducer = singleReducer<VendingOrgSettlementConfig>(
    REQUEST_SETTLEMENT_CONFIG,
    SUCCESS_SETTLEMENT_CONFIG,
    FAILURE_SETTLEMENT_CONFIG
);

/* Settlement Parties (external machine owners) */
export const settlementPartiesReducer = collectionReducer<VendingSettlementParty>(
    REQUEST_SETTLEMENT_PARTIES,
    SUCCESS_SETTLEMENT_PARTIES,
    FAILURE_SETTLEMENT_PARTIES
);

/* Product Billing (product name → article mapping) */
export const productBillingReducer = collectionReducer<VendingProductBillingConfig>(
    REQUEST_PRODUCT_BILLING,
    SUCCESS_PRODUCT_BILLING,
    FAILURE_PRODUCT_BILLING
);

/* Unmapped Products (purchased product names without billing mapping) */
export const unmappedProductsReducer = collectionReducer<string>(
    REQUEST_UNMAPPED_PRODUCTS,
    SUCCESS_UNMAPPED_PRODUCTS,
    FAILURE_UNMAPPED_PRODUCTS
);
